/**
 * Centralised axios client for the payment-tracker frontend (M25).
 *
 * The M22 backend introduced admin JWT auth on every non-health route,
 * but the frontend was still making raw axios calls with no auth
 * header. This module replaces those calls with a single axios
 * instance that:
 *
 *   1. **Injects the bearer token** from localStorage on every request.
 *   2. **Handles 401 → logout** by clearing storage and dispatching a
 *      ``bc:auth:logout`` window event so ``AuthContext`` can react
 *      without a circular import.
 *   3. **Propagates ``X-Request-ID``** so server logs can correlate
 *      client-side errors with backend traces.
 *   4. **Reads the base URL** from ``REACT_APP_BACKEND_URL`` once.
 *
 * Paths passed to ``api.get('/confirmations')`` are relative to
 * ``/api`` — the base URL already includes that prefix.
 */

import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) {
  // eslint-disable-next-line no-console
  console.error(
    "REACT_APP_BACKEND_URL is not set. Check apps/payment-tracker/frontend/.env",
  );
}

const API_BASE = `${BACKEND_URL || ""}/api`;

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
});

// ---------------------------------------------------------------------------
// Request interceptor — token + correlation id
// ---------------------------------------------------------------------------

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("pmt_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    config.headers["X-Request-ID"] = crypto.randomUUID();
  }
  return config;
});

// ---------------------------------------------------------------------------
// Response interceptor — 401 → logout
// ---------------------------------------------------------------------------

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      const wasAuthenticated = !!localStorage.getItem("pmt_token");
      if (wasAuthenticated) {
        localStorage.removeItem("pmt_token");
        delete api.defaults.headers.common.Authorization;
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("bc:auth:logout"));
        }
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Extract a human-readable error message from an axios error.
 *
 * Handles the ``bc_shared.errors`` envelope shape as well as the
 * legacy ``detail`` string shape, falling back to the raw axios
 * message.
 */
export function errorMessage(error) {
  if (!error) return "Unknown error";
  const data = error?.response?.data;
  if (data && typeof data === "object") {
    if (data.error && typeof data.error === "object" && data.error.message) {
      return String(data.error.message);
    }
    if (data.detail) return String(data.detail);
  }
  if (error.message) return String(error.message);
  return "Unknown error";
}

export default api;
